import { IConta } from "../../interfaces/IConta";
import { mapDadosBancarios } from "../../mappers/dadosBancarios/dadosBancarios.mapper";
import { api } from "../api";

type CampoFormatado = {
  numero: string;
  digito: string | null;
};

const extrairDigitoVerificador = (raw: string): CampoFormatado => {
  const valorLimpo = (raw || "").trim();
  const comSeparador = valorLimpo.match(/^([\dXx]+)[-\s]([\dXx])$/);

  if (comSeparador) {
    return {
      numero: comSeparador[1],
      digito: comSeparador[2].toUpperCase(),
    };
  }

  return {
    numero: valorLimpo,
    digito: null,
  };
};

/* =========================================
   TIPOS FORMATADOS
========================================= */

export type AgenciaFormatada = {
  agencia: string;
  digito: string | null;
};

export type ContaFormatada = {
  conta: string;
  digito: string | null;
};

/* =========================================
   FORMATAR AGÊNCIA
========================================= */

export const formatarAgencia = (raw: string): AgenciaFormatada => {
  const { numero, digito } = extrairDigitoVerificador(raw);

  return {
    agencia: numero,
    digito,
  };
};

/* =========================================
   FORMATAR CONTA
========================================= */

export const formatarConta = (raw: string): ContaFormatada => {
  const { numero, digito } = extrairDigitoVerificador(raw);

  return {
    conta: numero,
    digito,
  };
};

/* =========================================
   CONVERSÃO DO TIPO DE CONTA
========================================= */

export type TipoContaApi = "checking" | "savings";

const TIPO_CONTA_API: Record<string, TipoContaApi> = {
  corrente: "checking",
  poupanca: "savings",
};

export const mapearTipoConta = (tipoConta: string): TipoContaApi => {
  const tipo = TIPO_CONTA_API[tipoConta?.trim().toLowerCase()];

  if (!tipo) {
    throw new Error(`Tipo de conta inválido: ${tipoConta}`);
  }

  return tipo;
};

/* =========================================
   PAYLOAD DOS DADOS BANCÁRIOS
========================================= */

export type DadosBancariosPayload = {
  bankCode: string;
  branch: string;
  branchDigit: string | null;
  account: string;
  accountDigit: string | null;
  type: TipoContaApi;
  transferInterval?: string;
};

/* =========================================
   MONTAR PAYLOAD DOS DADOS BANCÁRIOS
========================================= */

export const montarPayloadDadosBancarios = (dadosBancarios: IConta): DadosBancariosPayload => {
  const agenciaTratada = formatarAgencia(dadosBancarios.agencia);
  const contaTratada = formatarConta(dadosBancarios.numeroConta);

  /* =======================================
     BANCO
  ======================================= */

  const bankCode = String(dadosBancarios.banco.codigo)
    .replace(/\D/g, "")
    .padStart(3, "0");

  /* =======================================
     PAYLOAD
  ======================================= */

  return {
    bankCode,
    branch: agenciaTratada.agencia,
    branchDigit: agenciaTratada.digito,
    account: contaTratada.conta,
    accountDigit: contaTratada.digito,
    type: mapearTipoConta(dadosBancarios.tipoConta),
  };
};

/* =========================================
   DADOS BANCÁRIOS CADASTRADOS
========================================= */

export type DadosBancariosCadastrados = {
  bankCode: string;
  branch: string;
  branchDigit: string | null;
  account: string;
  accountDigit: string | null;
  type: string;
  /* Dados retornados pela API */
  holderName?: string | null;
  holderDocument?: string | null;
  transferInterval?: string | null;
  transferEnabled?: boolean;
  kycStatus?: string;
  kycStatusReason?: string | null;
  gatewayStatus?: string;
};

/* =========================================
   RESPOSTA DA LOJA
========================================= */

type LojaComBanking = {
  banking: DadosBancariosCadastrados | null;
};

/* =========================================
   BUSCAR DADOS BANCÁRIOS
========================================= */

export const buscarDadosBancarios = async (lojaId: string): Promise<IConta | null> => {
  try {
    if (!lojaId) {
      throw new Error("ID da loja não informado para buscar os dados bancários.");
    }

    const response = await api.get<LojaComBanking>(`/stores/mine/${lojaId}`);
    const banking = response.data?.banking;

    if (!banking) {
      return null;
    }

    return mapDadosBancarios(banking);
  } catch (error: any) {
    throw error;
  }
};

/* =========================================
   ATUALIZAR LOJA
========================================= */

export interface AtualizarLojaPayload {
  name?: string;
  phone?: string;
  email?: string;
  categoryId?: string;
  description?: string;
  address?: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
    complement?: string;
  };
  businessHours?: string;
  banking?: DadosBancariosPayload;
}

export const atualizarLoja = async (lojaId: string, dadosLoja: AtualizarLojaPayload): Promise<void> => {
  try {
    if (!lojaId) {throw new Error("ID da loja não informado para atualização.");}
    if (!dadosLoja) {throw new Error("Dados da loja não informados.");}
    await api.patch(`/stores/${lojaId}`, dadosLoja);
  } catch (error: any) {
    throw error;
  }
};

/* =========================================
   KYC
========================================= */

export interface IKycLink {url: string;qrCodeBase64: string;expiresAt: string;}

/* =========================================
   GERAR LINK KYC
========================================= */

export const gerarLinkKyc = async (lojaId: string): Promise<IKycLink> => {
  try {
    if (!lojaId) {
      throw new Error("ID da loja não informado para gerar o link KYC.");
    }

    const response = await api.post<IKycLink>(`/stores/${lojaId}/kyc-link`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};